import { NavLink, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Offcanvas } from 'bootstrap'

import Icon from '../../../ui/Icon'
import Logo from '../../../ui/logo/Logo'
import { Button } from '../../../buttons/Button'
import { Buttons } from '../../../buttons/Buttons'

import { SOCIAL_LINKS } from '../../../../constants/socialLinks'
import {
  FeatureFlags,
  isFeatureEnabled,
} from '../../../../constants/featureFlags'

const GUEST_SIDENAV_ID = 'guest-sidenav'

const GUEST_LINKS = [
  { to: '/guest-clubs', labelKey: 'link.clubs', end: true },
  { to: '/guest-memberships', labelKey: 'link.memberships', end: true },
  { to: '/guest-booking', labelKey: 'link.bookings', end: true },
] as const

const hideGuestSidenav = (onHidden?: () => void) => {
  const sidebar = document.getElementById(GUEST_SIDENAV_ID)

  if (!sidebar) {
    onHidden?.()
    return
  }

  const offcanvas = Offcanvas.getOrCreateInstance(sidebar)

  if (!onHidden) {
    offcanvas.hide()
    return
  }

  const handleHidden = () => {
    sidebar.removeEventListener('hidden.bs.offcanvas', handleHidden)
    onHidden()
  }

  sidebar.addEventListener('hidden.bs.offcanvas', handleHidden)
  offcanvas.hide()
}

export const Header = () => {
  const { t } = useTranslation(['guestHeader', 'nav'])
  const navigate = useNavigate()

  const openMenu = () => {
    const sidebar = document.getElementById(GUEST_SIDENAV_ID)
    if (!sidebar) {
      return
    }

    Offcanvas.getOrCreateInstance(sidebar).show()
  }

  const goTo = (path: string) => {
    hideGuestSidenav(() => navigate(path))
  }

  return (
    <>
      <nav className="welcome-nav" aria-label={t('nav.label')}>
        <div className="welcome-nav-inner">
          {isFeatureEnabled(FeatureFlags.FF_REGISTER) && (
            <Button
              className="ph-iconbtn d-lg-none"
              variant="ghost"
              icon={<Icon name="menu" size={20} />}
              iconOnly
              label={t('btn.menu')}
              onClick={openMenu}
            />
          )}
          <div className="d-none d-lg-flex">
            <Logo />
          </div>
          {isFeatureEnabled(FeatureFlags.FF_REGISTER) && (
            <div className="links d-none d-lg-flex">
              {GUEST_LINKS.map(link => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.end}
                  className={({ isActive }) =>
                    isActive ? 'is-active' : undefined
                  }
                >
                  {t(link.labelKey)}
                </NavLink>
              ))}
            </div>
          )}
          <div className="welcome-nav-spacer" />
          <Buttons className="welcome-nav-actions">
            <div className="welcome-nav-social">
              {SOCIAL_LINKS.map(social => (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t(`social.${social.name}`)}
                  title={t(`social.${social.name}`)}
                >
                  <Icon name={social.name} size={20} />
                </a>
              ))}
            </div>

            {isFeatureEnabled(FeatureFlags.FF_REGISTER) && (
              <>
                <Button
                  className="d-none d-lg-inline-flex"
                  variant="ghost"
                  label={t('btn.login')}
                  onClick={() => navigate('/login')}
                />
                <Button
                  className="d-none d-lg-inline-flex"
                  label={t('btn.register')}
                  onClick={() => navigate('/register')}
                />
              </>
            )}
          </Buttons>
        </div>
      </nav>
      {isFeatureEnabled(FeatureFlags.FF_REGISTER) && (
        <aside
          id={GUEST_SIDENAV_ID}
          className="offcanvas offcanvas-start ph-mobile-side"
          tabIndex={-1}
          aria-labelledby="guest-sidebar-title"
        >
          <div className="offcanvas-header">
            <div id="guest-sidebar-title">
              <Logo />
            </div>
            <Button
              variant="ghost"
              className="ph-iconbtn mobile-side-close"
              icon={<Icon name="x" size={17} />}
              iconOnly
              label={t('btn.close')}
              data-bs-dismiss="offcanvas"
            />
          </div>
          <div className="offcanvas-body p-0">
            <div className="ph-side-section">
              {GUEST_LINKS.map(link => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.end}
                  className={({ isActive }) =>
                    `btn ph-side-link ${isActive ? 'active' : ''}`
                  }
                  onClick={event => {
                    event.preventDefault()
                    goTo(link.to)
                  }}
                >
                  {t(link.labelKey)}
                </NavLink>
              ))}
            </div>
            <div className="guest-sidenav-actions">
              {isFeatureEnabled(FeatureFlags.FF_REGISTER) && (
                <>
                  <Button
                    block
                    label={t('btn.register')}
                    onClick={() => goTo('/register')}
                  />
                  <Button
                    variant="ghost"
                    block
                    label={t('btn.login')}
                    onClick={() => goTo('/login')}
                  />
                </>
              )}
            </div>
          </div>
        </aside>
      )}
    </>
  )
}
